import React from 'react';
import { X, Shield, Globe, MapPin, Smartphone, Compass, Clock, Link } from 'lucide-react';

const TelemetryModal = ({
  selectedTelemetryVisitor,
  setSelectedTelemetryVisitor,
  getCountryFlag,
  getDeviceIcon,
  getBrowserIcon,
  formatFullDate
}) => {
  if (!selectedTelemetryVisitor) return null;

  const v = selectedTelemetryVisitor;
  const lat = Number(v.latitude) || (v.country === 'India' ? 13.0827 : 37.7749);
  const lng = Number(v.longitude) || (v.country === 'India' ? 80.2707 : -122.4194);
  const delta = 0.04;
  const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
  const embedMapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`;
  const hasCoords = Boolean(v.latitude && v.longitude);
  const hasGps = Boolean(v.gpsGranted);
  const coordSource = hasGps ? `🎯 GPS Exact (±${v.gpsAccuracy || '?'}m)` : '🌐 IP Geolocation (~10km)';
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <div className="modal-backdrop" onClick={() => setSelectedTelemetryVisitor(null)}>
      <div className="admin-modal-card telemetry-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <Shield className="modal-icon cyan" />
            <h3>VISITOR TELEMETRY DOSSIER</h3>
          </div>
          <button className="modal-close-btn" onClick={() => setSelectedTelemetryVisitor(null)}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body telemetry-grid">
          {/* IP & Network */}
          <div className="telemetry-block">
            <div className="block-title">
              <Globe className="block-icon" />
              <span>IP &amp; NETWORK PROTOCOL</span>
            </div>
            <div className="block-content">
              <div className="tele-row">
                <span className="lbl">IP Address:</span>
                <span className="val cyan font-mono">{v.ip}</span>
              </div>
              <div className="tele-row">
                <span className="lbl">ISP Provider:</span>
                <span className="val">{v.isp || 'Network Provider'}</span>
              </div>
              {v.org && (
                <div className="tele-row">
                  <span className="lbl">Network Org / ASN:</span>
                  <span className="val small font-mono">{v.org}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location & Geo */}
          <div className="telemetry-block">
            <div className="block-title">
              <MapPin className="block-icon" />
              <span>GEOGRAPHIC DATA</span>
            </div>
            <div className="block-content">
              <div className="tele-row">
                <span className="lbl">Location:</span>
                <span className="val">
                  {getCountryFlag(v.country)} {v.city || 'Unknown'}, {v.region || 'Unknown'}, {v.country || 'Unknown'}
                </span>
              </div>
              <div className="tele-row">
                <span className="lbl">Timezone:</span>
                <span className="val font-mono">{v.timezone || 'Asia/Kolkata'}</span>
              </div>
              {hasCoords && (
                <div className="tele-row">
                  <span className="lbl">Coordinates:</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="val-link">
                      {v.latitude}, {v.longitude} (Open Maps ↗)
                    </a>
                    <span style={{ fontSize: '0.7rem', color: hasGps ? '#00ff66' : '#94a3b8' }}>
                      {coordSource}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Hardware & Screen Specs */}
          <div className="telemetry-block">
            <div className="block-title">
              <Smartphone className="block-icon" />
              <span>HARDWARE &amp; DISPLAY</span>
            </div>
            <div className="block-content">
              <div className="tele-row">
                <span className="lbl">Device &amp; Memory:</span>
                <span className="val">{getDeviceIcon(v.device)} {v.device || 'Desktop'} {v.deviceMemory ? `(${v.deviceMemory} RAM)` : ''}</span>
              </div>
              <div className="tele-row">
                <span className="lbl">Screen Resolution:</span>
                <span className="val font-mono">{v.screenResolution || '1920x1080'} ({v.screenOrientation || 'landscape'})</span>
              </div>
              <div className="tele-row">
                <span className="lbl">Browser &amp; Theme:</span>
                <span className="val">{getBrowserIcon(v.browser)} {v.browser || 'Chrome'} ({v.preferredTheme || 'Dark'} Mode)</span>
              </div>
            </div>
          </div>

          {/* Regional & Session Campaign */}
          <div className="telemetry-block">
            <div className="block-title">
              <Compass className="block-icon" />
              <span>SESSION &amp; CAMPAIGN ORIGIN</span>
            </div>
            <div className="block-content">
              <div className="tele-row">
                <span className="lbl">Referrer Source:</span>
                <span className="val highlight">{v.referrer || 'Direct Entry'}</span>
              </div>
              {v.userLanguages && (
                <div className="tele-row">
                  <span className="lbl">System Languages:</span>
                  <span className="val small font-mono">{v.userLanguages}</span>
                </div>
              )}
              {v.utmSource && (
                <div className="tele-row">
                  <span className="lbl">UTM Source / Campaign:</span>
                  <span className="val cyan font-mono">{v.utmSource} / {v.utmCampaign || 'None'}</span>
                </div>
              )}
              <div className="tele-row">
                <span className="lbl">Entry Timestamp:</span>
                <span className="val">{formatFullDate(v.timestamp || v.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stored Location Interactive Map */}
        <div className="telemetry-map-container">
          <div className="map-header-bar">
            <div className="map-title-left">
              <MapPin size={14} className="map-pin-icon" />
              <span>STORED VISITOR ENTRY MAP FEED</span>
            </div>
            <span className="map-coords-badge">
              {lat.toFixed(4)}° N, {lng.toFixed(4)}° E ({v.city || 'Location'}, {v.country || 'Country'})
            </span>
          </div>
          <iframe
            title="Visitor Stored Entry Map"
            src={embedMapUrl}
            className="telemetry-map-iframe"
            loading="lazy"
          />
        </div>

        <div className="modal-footer">
          <button className="admin-action-btn primary-btn" onClick={() => setSelectedTelemetryVisitor(null)}>
            CLOSE DOSSIER
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelemetryModal;
